import { type ReactNode, type PropsWithChildren } from "react";
import { UNSAFE_PortalProvider as PortalProvider } from "react-aria";
import {
  DialogTrigger as DialogTriggerPrimitive,
  type PopoverProps as PopoverPrimitiveProps,
} from "react-aria-components";
import { type PropsWithOptionalTestID } from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";
import { CloseIcon } from "@boi/react-icons";

import * as S from "./TooltipPopoverV2.styled";
import {
  DEFAULT_OFFSET,
  DEFAULT_PLACEMENT,
} from "../TooltipV2/TooltipV2.constants";
import { Arrow } from "../TooltipV2/Arrow";
import { useOverlay } from "../OverlayContext";
import { Typography } from "../Typography";

export type TooltipPopoverV2Props = {
  children: ReactNode;
};

function TooltipPopoverV2({ children }: TooltipPopoverV2Props) {
  return children;
}

TooltipPopoverV2.Trigger = DialogTriggerPrimitive;

type TooltipPopoverV2ContentProps = PropsWithChildren &
  PropsWithOptionalTestID & {
    popoverProps?: Omit<PopoverPrimitiveProps, "children">;
    maxWidth?: number;
  };

TooltipPopoverV2.Content = function Content(
  props: TooltipPopoverV2ContentProps
) {
  const {
    popoverProps,
    maxWidth,
    children,
    testID = "tooltip-popover",
  } = props;

  const { tokens } = useDesignTokens();
  const { tooltip } = tokens;

  const { portalElement } = useOverlay();

  return (
    <PortalProvider getContainer={portalElement && (() => portalElement)}>
      <S.Popover
        offset={DEFAULT_OFFSET}
        placement={DEFAULT_PLACEMENT}
        {...popoverProps}
        data-testid={testID}
      >
        <Arrow />

        <S.Dialog>
          <S.Container $maxWidth={maxWidth}>
            <Typography
              color={tooltip.color}
              variant={tooltip.typography.variant}
              fontFamily={tooltip.typography.fontFamily}
            >
              {children}
            </Typography>

            <S.CloseButton slot="close" aria-label="Close tooltip">
              <CloseIcon />
            </S.CloseButton>
          </S.Container>
        </S.Dialog>
      </S.Popover>
    </PortalProvider>
  );
};

export default TooltipPopoverV2;
